import React from "react";
import LegalPage from "@/components/LegalPage";

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      lastUpdated="May 21, 2026"
      testid="terms-page"
    >
      <p>
        Welcome to Colleague. By using the service, you're agreeing to these
        terms. We've kept them as short and human-readable as we could while
        still covering what needs covering.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        1. What Colleague is
      </h2>
      <p>
        Colleague is a software service that lets you have AI-generated
        conversations with virtual "Colleague" personas about work, life, and
        the days in between. The personas are AI characters — not real humans
        and not licensed professionals. They are not a substitute for a doctor,
        therapist, lawyer, financial advisor, or any other regulated profession.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        2. Who can use Colleague
      </h2>
      <p>
        You must be at least 16 years old and able to enter into a contract in
        your jurisdiction. One person per account. You're responsible for
        keeping your password safe — if your account is misused, that's on you.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        3. Free trial and subscription
      </h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Every new account gets a 30-day free trial with full access.</li>
        <li>After 30 days, you need an active Colleague+ subscription to send new messages or use voice and avatar features.</li>
        <li>Colleague+ is $9.99/month or $99/year, payable in advance.</li>
        <li>We do not auto-renew without your action. You choose when to renew.</li>
        <li>Prices may change in the future — if they do, we'll email you at least 30 days before any change affects you.</li>
      </ul>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        4. What you can use Colleague for
      </h2>
      <p>You can use Colleague for personal, non-commercial conversations about your work and life. You may NOT:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Resell, repackage, or sublicense access to Colleague</li>
        <li>Use Colleague to harass, threaten, or harm anyone</li>
        <li>Use Colleague for unlawful purposes</li>
        <li>Attempt to extract our system prompts, jailbreak the personas, or scrape the service</li>
        <li>Use Colleague to make decisions of clinical, medical, legal, or financial gravity without confirming with a qualified human professional</li>
      </ul>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        5. The AI is AI
      </h2>
      <p>
        The personas can be warm, perceptive, and often very helpful. They can
        also be wrong, miss context, or say something that doesn't apply to
        your situation. Treat their replies as input, not instructions. Do not
        rely on Colleague for crisis support — if you or someone you know is in
        immediate distress, contact a crisis line (988 in the US) or local
        emergency services.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        6. Your content
      </h2>
      <p>
        Your conversations, profile, and personas belong to you. You grant us a
        limited license to process them for the purpose of running the service —
        nothing more. We never use them for advertising, model training, or any
        purpose besides making the service work for you.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        7. Service availability
      </h2>
      <p>
        We try hard to keep Colleague online. We don't guarantee it. If
        Colleague is down for an extended period and you're a Colleague+
        subscriber, contact us and we'll credit your account fairly.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        8. Account termination
      </h2>
      <p>
        You can close your account anytime — see the{" "}
        <a href="/contact" className="text-[#D4A574] hover:text-[#C99560] underline">
          Contact
        </a>{" "}
        page. We may suspend or close accounts that abuse the service, violate
        these terms, or chargeback subscription payments.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        9. Limitation of liability
      </h2>
      <p>
        Colleague is provided "as is." To the maximum extent allowed by law,
        our total liability to you for any claim arising from your use of the
        service is limited to the amount you paid us in the 12 months before
        the claim. We are not liable for indirect, incidental, or consequential
        damages.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        10. Governing law
      </h2>
      <p>
        These terms are governed by the laws of the United States and the
        State of [your state]. Disputes will be resolved in the courts of [your
        county/state]. (Solo-founder note: update these once you've registered
        the business.)
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        11. Changes to these terms
      </h2>
      <p>
        If we change these terms in a material way, we'll email you and post a
        note in the app at least 14 days before they take effect.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        12. Contact
      </h2>
      <p>
        Questions about these terms? Email us at{" "}
        <a
          href="mailto:hello@meetacolleague.com"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          hello@meetacolleague.com
        </a>
        .
      </p>
    </LegalPage>
  );
}
